import React, {Component} from "react";

import {gamesAPI} from "../../../../games/api/api";
import {resolveGameImageUrl} from "../../../../games/utils/game-image-url";
import {
    MachineGame,
    MachineRTPProfile,
    ServerMachine
} from "../../../types/machine";

import "./Machines.component.scss";

export const MACHINES_REFRESH_INTERVAL = 15000;
const DEFAULT_GAME_IMAGE = "/wave/games/static/game_files/default.png";

export const getCurrentMachineRTPProfile = (
    machine: ServerMachine
): MachineRTPProfile | null => {
    const profiles =
        machine && Array.isArray(machine.rtp_profiles)
            ? machine.rtp_profiles
            : [];
    const currentGame = machine && machine.game;

    if (profiles.length === 0) return null;
    if (!currentGame) return profiles[0];

    return (
        profiles.find(
            profile =>
                `${profile.id}` === `${currentGame.id}` ||
                profile.name === currentGame.name
        ) || profiles[0]
    );
};

export const getMachineGameIds = (machines?: ServerMachine[]) =>
    Array.from(
        new Set(
            (Array.isArray(machines) ? machines : []).reduce<number[]>(
                (gameIds, machine) => {
                    if (machine && machine.game) {
                        gameIds.push(machine.game.id);
                    }
                    return gameIds;
                },
                []
            )
        )
    ).sort((first, second) => first - second);

export const getMachineGameImagePath = (game?: MachineGame | null) => {
    return (game && game.image) || DEFAULT_GAME_IMAGE;
};

export const getMachineConnectionStatus = (machine: ServerMachine) =>
    (machine.status_details && machine.status_details.connection) ||
    machine.connection_status ||
    "not_logged";

export const hasMachineCredit = (machine: ServerMachine) =>
    Boolean(
        machine.counters &&
            machine.counters.credit &&
            machine.counters.credit > 0
    );

interface MachineCardProps {
    machine: ServerMachine;
    gameDetails?: MachineGame;
}

export const MachineCard = ({machine, gameDetails}: MachineCardProps) => {
    const currentGame = machine.game;
    const rtpProfile = getCurrentMachineRTPProfile(machine);
    const connectionStatus = getMachineConnectionStatus(machine);
    const isConnected = connectionStatus === "logged";
    const hasCredit = hasMachineCredit(machine);
    const credit =
        machine.counters && machine.counters.credit
            ? machine.counters.credit
            : 0;
    const connectionDescription = isConnected
        ? "Machine connected"
        : "Machine not connected";
    const creditDescription = hasCredit
        ? `Machine has credit (${credit})`
        : "Machine has no credit";
    const vlt =
        machine.settings && machine.settings.vlt !== null &&
        machine.settings.vlt !== undefined
            ? machine.settings.vlt
            : "--";
    const fallbackImage = resolveGameImageUrl(DEFAULT_GAME_IMAGE);
    const gameImage = resolveGameImageUrl(
        getMachineGameImagePath(gameDetails)
    );

    return (
        <article className="machine-card">
            <div className="machine-game-image">
                <img
                    src={gameImage}
                    alt={currentGame ? currentGame.name : "No current game"}
                    onError={event => {
                        if (event.currentTarget.src !== fallbackImage) {
                            event.currentTarget.src = fallbackImage;
                        }
                    }}
                />
                <span>
                    {currentGame ? `Game ID ${currentGame.id}` : "No Game ID"}
                </span>
            </div>
            <div className="machine-card-content">
                <div className="machine-game-heading">
                    <h2>
                        {currentGame ? currentGame.name : "No current game"}
                    </h2>
                    <div className="machine-vlt-information">
                        <span>VLT:</span>
                        <strong>{vlt}</strong>
                    </div>
                </div>
                <div className="machine-status-indicators">
                    <i
                        className={`icon-wifi-solid ${
                            isConnected ? "green" : "red"
                        }`}
                        role="img"
                        aria-label={connectionDescription}
                        title={connectionDescription}
                    />
                    <strong
                        className={hasCredit ? "green" : "red"}
                        role="img"
                        aria-label={creditDescription}
                        title={creditDescription}
                    >
                        $
                    </strong>
                </div>
                {rtpProfile && (
                    <div className="machine-rtp-information">
                        <div>
                            <span>Active RTP</span>
                            <strong>
                                {rtpProfile.active_profile
                                    ? rtpProfile.active_profile
                                    : "Not reported"}
                            </strong>
                        </div>
                        <div>
                            <span>Configured RTP</span>
                            <strong>
                                {rtpProfile.configured_profile
                                    ? rtpProfile.configured_profile
                                    : "Not configured"}
                            </strong>
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
};

interface MachinesProps {
    isOnline: boolean;
    machines?: ServerMachine[];
    onRefresh: () => void;
}

interface MachinesState {
    gamesById: {[id: string]: MachineGame};
}

class Machines extends Component<MachinesProps, MachinesState> {
    private refreshInterval: number;
    private requestedGameIds = "";
    private mounted = false;

    state: MachinesState = {
        gamesById: {}
    };

    componentDidMount() {
        this.mounted = true;
        this.refreshInterval = window.setInterval(
            this.refresh,
            MACHINES_REFRESH_INTERVAL
        );
        this.loadGames(this.props);
    }

    componentDidUpdate(previousProps: MachinesProps) {
        const previousGameIds = getMachineGameIds(
            previousProps.machines
        ).join(",");
        const currentGameIds = getMachineGameIds(this.props.machines).join(",");

        if (
            previousProps.isOnline !== this.props.isOnline ||
            previousGameIds !== currentGameIds
        ) {
            this.loadGames(this.props);
        }
    }

    componentWillUnmount() {
        this.mounted = false;
        window.clearInterval(this.refreshInterval);
    }

    loadGames = (props: MachinesProps) => {
        if (!props.isOnline) {
            this.requestedGameIds = "";
            return;
        }

        const gameIds = getMachineGameIds(props.machines);
        const requestKey = gameIds.join(",");

        if (requestKey === this.requestedGameIds) return;
        this.requestedGameIds = requestKey;

        if (gameIds.length === 0) {
            if (Object.keys(this.state.gamesById).length > 0) {
                this.setState({gamesById: {}});
            }
            return;
        }

        gamesAPI.getGamesByIds(gameIds).then(
            response => {
                if (!this.mounted || this.requestedGameIds !== requestKey) {
                    return;
                }

                const games =
                    response &&
                    response.data &&
                    Array.isArray(response.data.results)
                        ? response.data.results
                        : [];
                const gamesById = games.reduce((result, game) => {
                    result[`${game.id}`] = game;
                    return result;
                }, {});

                this.setState({gamesById});
            },
            () => {
                if (this.mounted && this.requestedGameIds === requestKey) {
                    this.requestedGameIds = "";
                    this.setState({gamesById: {}});
                }
            }
        );
    };

    refresh = () => {
        this.props.onRefresh();
        this.loadGames(this.props);
    };

    render() {
        if (!this.props.isOnline) return null;

        const machines = Array.isArray(this.props.machines)
            ? this.props.machines
            : [];

        return (
            <section className="machines-panel">
                <div className="machines-panel-header">
                    <h1>Machines</h1>
                    <span>{machines.length}</span>
                </div>
                {machines.length > 0 ? (
                    <div className="machine-card-list">
                        {machines.map(machine => (
                            <MachineCard
                                key={machine.id}
                                machine={machine}
                                gameDetails={
                                    machine.game
                                        ? this.state.gamesById[
                                              `${machine.game.id}`
                                          ]
                                        : undefined
                                }
                            />
                        ))}
                    </div>
                ) : (
                    <div className="no-machines">No machines reported.</div>
                )}
            </section>
        );
    }
}

export default Machines;
